import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { Text } from '@chakra-ui/react';
import { formatSIRET, isSIRET } from '@shinetools/vat-siren-siret';

import useCompanyContext from 'common/hooks/useCompanyContext';
import { throwOnlyUnhandledErrors } from 'common/hooks/useErrorHandledMutation';
import Button from 'components/_core/Button';
import * as Form from 'components/_core/form';
import Header from 'features/Onboarding/components/Header';

import Layout from '../../Layout';
import {
  LegalRegistrationNumberRequiredFragmentDoc,
  UpdateMissingLegalRegistrationNumberDocument,
} from '../../updateMissingLegalRegistrationNumber.gql';
import illustration from './assets/document-seal.svg';
import { FetchAndAddRegistrationDocumentDocument } from './fetchAndAddRegistrationDocument.gql';
import locales from './locales';

interface FormInput {
  siret: string;
}

const SiretWallForm = () => {
  const history = useHistory();
  const { companyProfileId } = useCompanyContext();

  if (!companyProfileId) {
    throw new Error('companyProfileId cannot be undefined');
  }

  const [fetchAndAddRegistrationDocument, { error, loading: documentLoading }] =
    useMutation(FetchAndAddRegistrationDocumentDocument);
  if (error) {
    throwOnlyUnhandledErrors(error);
  }
  const [
    updateMissingLegalRegistrationNumber,
    { error: updateError, loading: updateLoading },
  ] = useMutation(UpdateMissingLegalRegistrationNumberDocument, {
    update(cache, { data }) {
      if (data?.updateCompanyProfile.company?.legalRegistrationNumber) {
        cache.writeFragment({
          data: {
            __typename: 'Company',
            companyProfileId,
            isLegalRegistrationNumberRequired: false,
          },
          fragment: LegalRegistrationNumberRequiredFragmentDoc,
        });
      }
    },
  });
  const {
    control,
    formState: { isValid },
    handleSubmit,
  } = useForm<FormInput>({
    mode: 'onChange',
  });
  if (updateError) {
    throwOnlyUnhandledErrors(updateError);
  }

  const loading = documentLoading || updateLoading;

  const onSubmit = async ({ siret }: FormInput) => {
    const legalRegistrationNumber = siret.replace(/\s/g, '');

    const { data } = await fetchAndAddRegistrationDocument({
      variables: { input: { companyProfileId, legalRegistrationNumber } },
    });

    if (data) {
      const { fetchAndAddRegistrationDocument: result } = data;

      // Fetching the registration document succeeded
      if (result?.kybDocument) {
        await updateMissingLegalRegistrationNumber({
          variables: { input: { companyProfileId, legalRegistrationNumber } },
        });
        history.push('/siret-wall/success');
      } else {
        history.push(
          `/siret-wall/upload?legalRegistrationNumber=${legalRegistrationNumber}`,
        );
      }
    }
  };

  return (
    <Layout allowBack>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Header imageSrc={illustration} title={locales.headline} />
        <Controller
          control={control}
          name="siret"
          render={({ field: { onChange, value }, field }) => (
            <Form.Input
              type="string"
              {...field}
              onChange={({ target }) => onChange(formatSIRET(target.value))}
              placeholder={locales.placeholder}
              value={value || ''}
            />
          )}
          rules={{
            required: true,
            validate: (value) => isSIRET(value.replace(/\s/g, '')),
          }}
        />
        <Text color="grey.0" marginTop="space-4" size="xs">
          {locales.hint}
        </Text>
        <Button
          isDisabled={!isValid || loading}
          isExpanded
          isLoading={loading}
          marginTop="space-24"
          type="submit"
        >
          {locales.continue}
        </Button>
      </form>
    </Layout>
  );
};

export default SiretWallForm;
